import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  OnInit,
  QueryList,
  signal,
  ViewChildren,
  WritableSignal,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  EEntityType,
  EFooterBlockType,
  ERelationType,
  FOOTER_BLOCK_TYPE_OPTIONS,
  INormalText,
  ITextLanguageValue,
} from '@web/dtos';
import { map, Observable } from 'rxjs';
import { ButtonComponent } from '../../../components/feedback/button/button.component';
import { IDialogData } from '../../../components/dialogs/dialog';
import { ResetChangesDialogComponent } from '../../../components/dialogs/reset-changes-dialog/reset-changes-dialog.component';
import { ImageSelectComponent } from '../../../components/data-input/form-inputs/image-select/image-select.component';
import { AccessibilityTextInputComponent } from '../../../components/data-input/form-inputs/accessibility-text-input/accessibility-text-input.component';
import { TranslationTextInputComponent } from '../../../components/data-input/form-inputs/translation-text-input/translation-text-input.component';
import { LoadingAnimationComponent } from '../../../components/feedback/loading-animation/loading-animation.component';
import { RevealFormErrorsButtonComponent } from '../../../components/feedback/reveal-form-errors-button/reveal-form-errors-button.component';
import { SectionHeaderActionsComponent } from '../../../components/layout/section/section-header/section-header-actions/section-header-actions.component';
import { SectionHeaderComponent } from '../../../components/layout/section/section-header/section-header.component';
import { SectionHeadlineComponent } from '../../../components/layout/section/section-header/section-headline/section-headline.component';
import { SectionComponent } from '../../../components/layout/section/section.component';
import { IFooterForm } from '../../../entities/footer-entity';
import { ILinkForm } from '../../../entities/link-entity';
import { FooterFormsFactoryService } from '../../../factory/footer/footer-forms-factory.service';
import { LinkFormsFactoryService } from '../../../factory/link/link-forms-factory.service';
import { FooterStore } from '../../../services/api/footer/footer-store.service';
import { FormTracking } from '../form-tracking/form-tracking.component';
import { ElementFocusService } from '../../../services/support/element-focus-support/element-focus-support.service';
import { DialogService } from '../../../services/support/dialog/dialog.service';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';
import { DragDropGrid } from '../../../components/data-input/drag-drop-grid/drag-drop-grid';
import { CheckboxComponent } from '../../../components/data-input/form-inputs/checkbox/checkbox.component';
import { ColorPickerListComponent } from '../../../components/data-input/color-picker-list/color-picker-list';
import { FooterBlockEditorComponent } from './block-editors/footer-block-editor.component';
import { LanguagePipe } from '../../../pipes/language/language.pipe';

@Component({
  selector: 'aw-footer-page',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SectionComponent,
    ButtonComponent,
    SectionHeaderComponent,
    SectionHeaderActionsComponent,
    SectionHeadlineComponent,
    ImageSelectComponent,
    AccessibilityTextInputComponent,
    TranslationTextInputComponent,
    LoadingAnimationComponent,
    RevealFormErrorsButtonComponent,
    DragDropGrid,
    CheckboxComponent,
    ColorPickerListComponent,
    FooterBlockEditorComponent,
    LanguagePipe,
  ],
  templateUrl: './footer-page.component.html',
})
export class FooterPageComponent
  extends FormTracking<IFooterForm>
  implements OnInit
{
  private readonly elementFocusService: ElementFocusService =
    inject(ElementFocusService);
  readonly footerStore: FooterStore = inject(FooterStore);
  readonly formBuilder: FormBuilder = inject(FormBuilder);
  readonly footerFormsFactory: FooterFormsFactoryService = inject(
    FooterFormsFactoryService
  );

  readonly linkFormsFactory: LinkFormsFactoryService = inject(
    LinkFormsFactoryService
  );
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  private readonly languagePipe: LanguagePipe = inject(LanguagePipe);
  private readonly dialogService: DialogService = inject(DialogService);

  @ViewChildren(SectionComponent)
  private readonly footerSections!: QueryList<SectionComponent>;

  protected areAllSectionsCollapsed(): boolean {
    const sections = this.footerSections?.filter(section =>
      section.isCollapsible()
    );
    return !!sections?.length && sections.every(section => section.isCollapsed);
  }

  protected toggleAllSections(): void {
    const shouldExpand = this.areAllSectionsCollapsed();
    this.footerSections.forEach(section => {
      if (section.isCollapsible() && section.isCollapsed === shouldExpand) {
        section.toggleCollapse();
      }
    });
  }

  protected readonly EntityType: typeof EEntityType = EEntityType;
  public footerForm!: FormGroup<IFooterForm>;
  protected readonly isSaving: WritableSignal<boolean> = signal(false);
  private readonly rebuildFormAfterReload: WritableSignal<boolean> =
    signal(false);
  protected readonly TextRelationType: typeof ERelationType = ERelationType;
  protected readonly footerBlockIcons: Map<EFooterBlockType, string> = new Map<
    EFooterBlockType,
    string
  >(FOOTER_BLOCK_TYPE_OPTIONS.map(option => [option.value, option.icon]));

  private readonly mandatoryFooterLinkPaths: Set<string> = new Set<string>([
    'impressum',
    'datenschutz',
  ]);

  public isMandatoryFooterLink(linkFormGroup: FormGroup<ILinkForm>): boolean {
    const urls: INormalText[] = linkFormGroup.controls.urls.value;

    return urls.some((url: INormalText): boolean =>
      url.text.values.some((value: ITextLanguageValue): boolean =>
        this.mandatoryFooterLinkPaths.has(this.getLinkPath(value.value))
      )
    );
  }

  public getBlockLetter(index: number): string {
    return String.fromCharCode(65 + index);
  }

  private getLinkPath(value: string): string {
    return (
      value
        .trim()
        .toLowerCase()
        .split('?')[0]
        .replace(/\/+$/, '')
        .split('/')
        .pop() ?? ''
    );
  }

  public onHiddenBlocksChanged(hiddenBlocks: string[]): void {
    const hiddenBlocksControl =
      this.footerForm.controls.layout.controls.hiddenBlocks;

    hiddenBlocksControl.clear();
    hiddenBlocks.forEach(id =>
      hiddenBlocksControl.push(this.formBuilder.nonNullable.control(id))
    );

    this.footerForm.markAsDirty();
  }

  public onBlockOrderChanged(blockOrder: string[]): void {
    const blockOrderControl =
      this.footerForm.controls.layout.controls.blockOrder;

    blockOrderControl.clear();
    blockOrder.forEach(id =>
      blockOrderControl.push(this.formBuilder.nonNullable.control(id))
    );

    this.footerForm.markAsDirty();
  }
  public getCustomLayoutBlocks(): Array<{
    id: string;
    name: string;
    icon: string;
  }> {
    return this.footerForm.controls.blocks.controls.map((block, index) => {
      const defaultName = `${this.cmsTextProvider.footer.block} ${this.getBlockLetter(index)}`;
      return {
        id: block.controls.id.value as string,
        name:
          this.languagePipe.transform(block.controls.headlines.value).trim() ||
          defaultName,
        icon: this.footerBlockIcons.get(block.controls.type.value) ?? 'article',
      };
    });
  }

  constructor() {
    super();
    effect(() => {
      if (
        !this.footerStore.hasLoaded() ||
        this.footerStore.isLoading() ||
        (this.footerForm && !this.rebuildFormAfterReload())
      ) {
        return;
      }
      this.rebuildFormAfterReload.set(false);
      const footer = this.footerStore.footer();
      this.footerForm = this.footerFormsFactory.buildForm(
        this.deepCopyService.createDeepCopy(footer)
      );
      this.initFormTracking(this.footerForm);
    });
    effect(() => {
      this.elementFocusService.focusElement(
        !this.footerStore.isLoading() && this.footerStore.hasLoaded()
      );
    });
  }

  ngOnInit(): void {
    window.onbeforeunload = (event: BeforeUnloadEvent) => {
      if (this.footerForm?.dirty) {
        event.preventDefault();
      }
    };
  }

  addLink(event: MouseEvent): void {
    (event.target as HTMLElement).blur();
    this.footerForm.controls.linkArea.controls.links.push(
      this.linkFormsFactory.buildLinkFormGroup(
        undefined,
        this.footerForm.controls.linkArea.controls.links.controls.length
      )
    );
    this.footerForm.markAsDirty();
  }

  deleteLink(index: number): void {
    this.footerForm.controls.linkArea.controls.links.removeAt(index);
    this.footerForm.markAsDirty();
  }

  onColorChosen(color: string, key: string): void {
    (this.footerForm.get('appearance') as FormGroup)
      .get(key)
      ?.patchValue(color.startsWith('#') ? color : '#' + color);

    this.footerForm.markAsDirty();
  }

  async save(): Promise<boolean> {
    if (!this.footerForm.valid) {
      this.footerForm.markAllAsTouched();
      return false;
    }
    if (this.isSaving()) return false;

    const formSnapshot = JSON.stringify(this.footerForm.getRawValue());
    this.isSaving.set(true);
    try {
      await this.footerStore.save(
        this.footerFormsFactory.buildEntity(this.footerForm)
      );
      if (formSnapshot === JSON.stringify(this.footerForm.getRawValue())) {
        this.footerForm.markAsPristine();
        this.initFormTracking(this.footerForm);
      }
      return true;
    } finally {
      this.isSaving.set(false);
    }
  }

  resetFormChanges(): void {
    this.rebuildFormAfterReload.set(true);
    this.footerStore.reload();
  }

  public handleAbortChangesDialog(): Observable<boolean> {
    const dialogRef = this.dialogService.openDialog<IDialogData>(
      ResetChangesDialogComponent,
      {
        headline: this.cmsTextProvider.shared.revertChanges,
        width: '600px',
        icon: 'warning',
        iconBackgroundColor: '#eed202',
      }
    );
    dialogRef.closed.subscribe(result => {
      if (result != undefined && result.submit) {
        if (result.acceptChanges) {
          this.save();
        } else {
          this.resetFormChanges();
        }
      }
    });
    return dialogRef.closed.pipe(map(result => result?.submit ?? false));
  }

  public moveItemAtIndex(
    formArray: FormArray,
    index: number,
    newIndex: number
  ): void {
    const control = formArray.at(index);

    formArray.removeAt(index, { emitEvent: false });
    formArray.insert(newIndex, control, { emitEvent: false });

    formArray.markAsDirty();
    formArray.updateValueAndValidity();
    this.footerForm.markAsDirty();
  }
}
